import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  EquipmentId,
  type CleaningProcedureId,
  type CleaningRecordId,
  type EquipmentTypeId,
} from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CleaningPropertyList } from "@/features/cleaning-form";
import { ArrowLeftIcon } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute(
  "/inventory/equipment/$equipment_id/cleaning/new",
)({
  component: NewCleaningRecordPage,
  params: z.object({
    equipment_id: z.coerce.number().transform((val) => val as EquipmentId),
  }),
});

function NewCleaningRecordPage() {
  const { equipment_id } = Route.useParams();
  const navigate = useNavigate();

  const { data: equipment } = trpc.equipment.get.useQuery(equipment_id);

  const [procedureId, setProcedureId] = useState<CleaningProcedureId | null>(
    null,
  );

  const { data: procedures } = trpc.cleaning.procedures.list.useQuery(
    {
      isActive: true,
      equipmentTypeId: equipment?.equipmentModel.equipmentType
        .id as EquipmentTypeId,
    },
    { enabled: !!equipment },
  );

  const { data: defaultForm } = trpc.cleaning.forms.getDefault.useQuery(
    procedureId!,
    { enabled: !!procedureId },
  );

  const handleRecordCreated = (recordId: CleaningRecordId) => {
    navigate({
      to: "/inventory/equipment/$equipment_id/cleaning/$record_id",
      params: { equipment_id, record_id: recordId },
      replace: true,
    });
  };

  return (
    <div className="flex-1 p-6 space-y-4 max-w-2xl">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            navigate({
              to: "/inventory/equipment/$equipment_id/cleaning",
              params: { equipment_id },
            })
          }
          className="text-muted-foreground"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Cleaning
        </Button>
      </div>

      <h2 className="text-sm font-medium">New Cleaning Record</h2>

      <div className="space-y-2">
        <Label>Cleaning Procedure</Label>
        <Select
          value={procedureId ? String(procedureId) : ""}
          onValueChange={(v) =>
            setProcedureId(Number(v) as CleaningProcedureId)
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a procedure..." />
          </SelectTrigger>
          <SelectContent>
            {procedures?.map((proc) => (
              <SelectItem key={proc.id} value={String(proc.id)}>
                {proc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {procedureId && defaultForm && (
        <CleaningPropertyList
          formId={defaultForm.id}
          equipmentId={equipment_id}
          isEditable
          onRecordCreated={handleRecordCreated}
        />
      )}

      {procedureId && !defaultForm && (
        <p className="text-sm text-muted-foreground">
          No default form found for this procedure.
        </p>
      )}
    </div>
  );
}
